import { Anchor, Button, Group, Loader, Text } from '@mantine/core';

type Props = Readonly<{
  ready: boolean;
  timedOut: boolean;
  stopped?: boolean;
  onSwitch: () => void;
}>;

const supportLink = (
  <Anchor href="mailto:support@cloudhouse.com" fz="sm" c="var(--text-default)" underline="always">
    support@cloudhouse.com
  </Anchor>
);

function message(ready: boolean, timedOut: boolean, stopped: boolean) {
  if (ready) return "Your organisation's address is ready.";
  if (stopped) {
    return (
      <>
        Your organisation's address is taking unusually long, so we've stopped checking automatically
        — refresh the page to check again, or contact {supportLink}.
      </>
    );
  }
  if (timedOut) {
    return (
      <>
        Setting up your organisation's address is taking longer than expected — you can keep working
        here, or contact {supportLink} if it persists.
      </>
    );
  }
  return "We're setting up your organisation's address — this usually takes about 10 minutes. You can start using the Hub here in the meantime.";
}

function tone(ready: boolean, timedOut: boolean, stopped: boolean) {
  if (ready) return 'var(--status-success)';
  if (timedOut || stopped) return 'var(--status-warning)';
  return 'var(--text-subtle)';
}

// Non-blocking notice shown on the neutral hub host while a freshly-created org's own host is
// still provisioning. It becomes actionable ("Switch now") once that host answers; we never move
// the user automatically once they've started working here.
export function HostReadyBanner({ ready, timedOut, stopped = false, onSwitch }: Props) {
  const colour = tone(ready, timedOut, stopped);
  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      gap="sm"
      px="md"
      py="xs"
      role="status"
      style={{
        flexShrink: 0,
        background: `color-mix(in srgb, ${colour}, var(--bg-surface) 90%)`,
        borderBottom: `1px solid color-mix(in srgb, ${colour}, transparent 65%)`,
      }}
    >
      <Group gap={8} wrap="nowrap">
        {!ready && !timedOut && !stopped && <Loader size={14} color="var(--text-subtle)" />}
        <Text size="sm" c="var(--text-default)">
          {message(ready, timedOut, stopped)}
        </Text>
      </Group>
      {ready && (
        <Button
          size="xs"
          variant="light"
          onClick={onSwitch}
          style={{ flexShrink: 0, '--button-color': colour }}
        >
          Switch now
        </Button>
      )}
    </Group>
  );
}
